import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { siteInfo } from '../data/mockData';
import { toast } from '../hooks/use-toast';

const Contato = () => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    assunto: '',
    mensagem: ''
  });

  // Google Ads Tag
  useEffect(() => {
    // Carregar o script do gtag.js
    const script = document.createElement('script');
    script.src = 'https://www.googletagmanager.com/gtag/js?id=AW-18052643578';
    script.async = true;
    document.head.appendChild(script);

    // Inicializar o gtag após o script carregar
    script.onload = () => {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('config', 'AW-18052643578');
    };

    // Cleanup
    return () => {
      if (script.parentNode) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock submission
    console.log('Form submitted:', formData);
    
    // Evento de conversão Google Ads
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-18052643578',
        'event_category': 'contact',
        'event_label': 'form_submission'
      });
    }

    // DataLayer event
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'form_submit',
      form_name: 'contato',
      form_data: {
        nome: formData.nome,
        assunto: formData.assunto
      }
    });
    
    toast({
      title: "Mensagem enviada com sucesso!",
      description: "Em breve entraremos em contato. Obrigado!",
    });
    
    setFormData({
      nome: '',
      email: '',
      telefone: '',
      assunto: '',
      mensagem: ''
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center text-white bg-gradient-to-r from-lime-600 to-teal-500">
        <div className="text-center px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Contato</h1>
          <p className="text-xl text-teal-50">Estamos aqui para ajudar 24 horas por dia</p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 -mt-24 relative z-10">
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-7 h-7 text-lime-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Telefone</h3>
              <a href={`tel:${siteInfo.phone.replace(/\D/g, '')}`} className="text-lime-600 hover:text-teal-600">
                {siteInfo.phone}
              </a>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-7 h-7 text-lime-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">E-mail</h3>
              <a href={`mailto:${siteInfo.email}`} className="text-lime-600 hover:text-teal-600 text-sm break-all">
                {siteInfo.email}
              </a>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-7 h-7 text-lime-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Localização</h3>
              <p className="text-gray-600 text-sm">{siteInfo.address}</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-7 h-7 text-lime-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Horário</h3>
              <p className="text-gray-600 text-sm">Atendimento 24h<br />Todos os dias</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Envie sua mensagem</h2>
            <p className="text-gray-600 text-lg">Preencha o formulário abaixo e entraremos em contato o mais breve possível</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-gray-50 rounded-2xl shadow-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-2">
                  Nome completo *
                </label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                  placeholder="Seu nome"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  E-mail *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                  placeholder="seu@email.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="telefone" className="block text-sm font-medium text-gray-700 mb-2">
                  Telefone *
                </label>
                <input
                  type="tel"
                  id="telefone"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                  placeholder="(21) 9999-9999"
                />
              </div>

              <div>
                <label htmlFor="assunto" className="block text-sm font-medium text-gray-700 mb-2">
                  Assunto *
                </label>
                <select
                  id="assunto"
                  name="assunto"
                  value={formData.assunto}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                >
                  <option value="">Selecione um assunto</option>
                  <option value="Cremação">Cremação</option>
                  <option value="Translado">Translado</option>
                  <option value="Sepultamento">Sepultamento</option>
                  <option value="Informações Gerais">Informações Gerais</option>
                  <option value="Outro">Outro</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="mensagem" className="block text-sm font-medium text-gray-700 mb-2">
                Mensagem *
              </label>
              <textarea
                id="mensagem"
                name="mensagem"
                value={formData.mensagem}
                onChange={handleChange}
                required
                rows="6"
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors resize-none"
                placeholder="Como podemos ajudá-lo?"
              ></textarea>
            </div>

            <button
              type="submit"
              className="w-full bg-teal-500 hover:bg-lime-600 text-white px-8 py-4 rounded-md text-lg font-medium transition-all duration-300 hover:shadow-lg hover:shadow-teal-500/50 flex items-center justify-center space-x-2"
            >
              <Send className="w-5 h-5" />
              <span>Enviar Mensagem</span>
            </button>
          </form>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">Nossa Localização</h2>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3675.123!2d-43.2791!3d-22.9044!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x997e5c7a5c5c5c5d%3A0x1234567890abcdef!2sRua%20En%C3%A9as%20Galv%C3%A3o%2C%2078%20-%20M%C3%A9ier%2C%20Rio%20de%20Janeiro%20-%20RJ%2C%2020775-120!5e0!3m2!1spt-BR!2sbr!4v1234567890123!5m2!1spt-BR!2sbr"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localização Santa Casa Serviço Funerário Imediato - Rua Enéas Galvão, 78 - Sala 201 - Meier, RJ"
            ></iframe>
          </div>
          <div className="mt-6 text-center bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Como Chegar</h3>
            <p className="text-gray-700 mb-2">
              <strong>Endereço:</strong> {siteInfo.address}
            </p>
            <p className="text-gray-700 mb-2">
              <strong>CEP:</strong> 20725-001
            </p>
            <p className="text-gray-600 text-sm mb-4">
              Estamos localizados na Rua Dias da Cruz, no coração do Meier, Zona Norte do Rio de Janeiro.
              Fácil acesso por transporte público e com estacionamento próximo.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="https://www.google.com/maps/dir//Rua+Dias+da+Cruz,+215+-+Meier,+Rio+de+Janeiro+-+RJ,+20725-001"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-md font-medium transition-colors"
              >
                <MapPin className="w-5 h-5 mr-2" />
                Abrir no Google Maps
              </a>
              <a
                href="https://waze.com/ul?q=Rua+Dias+da+Cruz+215+Meier+Rio+de+Janeiro"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-gradient-to-r from-lime-400 to-lime-500 hover:from-lime-500 hover:to-lime-600 text-gray-900 px-6 py-3 rounded-xl font-bold transition-colors"
              >
                Abrir no Waze
              </a>
            </div>
          </div>

          {/* SEO Content for Location */}
          <div className="mt-8 prose prose-lg max-w-none">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4 text-center">
              Funerária no Meier - Zona Norte do Rio de Janeiro
            </h3>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 mb-4">
                A <strong>Santa Casa Serviço Funerário Imediato</strong> está estrategicamente localizada na <strong>Rua Dias da Cruz, 215, Meier</strong>, 
                um dos bairros mais tradicionais da <strong>Zona Norte do Rio de Janeiro</strong>. Nossa <strong>funerária no Meier</strong> 
                oferece fácil acesso para famílias de toda a região, incluindo Engenho Novo, Méier, Todos os Santos, Cachambi, 
                Engenho de Dentro e adjacências.
              </p>
              <p className="text-gray-700 mb-4">
                Com <strong>atendimento 24 horas</strong>, nossa <strong>funerária na Zona Norte RJ</strong> está sempre pronta para 
                atender famílias que precisam de <strong>serviços funerários imediatos no Meier</strong>, oferecendo todo suporte 
                necessário em momentos de luto.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div className="bg-white rounded-lg p-4 shadow">
                  <h4 className="font-semibold text-gray-900 mb-2">Regiões Atendidas:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Meier e adjacências</li>
                    <li>• Engenho Novo</li>
                    <li>• Todos os Santos</li>
                    <li>• Cachambi</li>
                    <li>• Engenho de Dentro</li>
                    <li>• Abolição</li>
                    <li>• Pilares</li>
                    <li>• Toda Zona Norte RJ</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4 shadow">
                  <h4 className="font-semibold text-gray-900 mb-2">Transporte Público:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• <strong>Metrô:</strong> Estação Méier (Linha 2)</li>
                    <li>• <strong>Trem:</strong> Estação Méier (SuperVia)</li>
                    <li>• <strong>Ônibus:</strong> Diversas linhas</li>
                    <li>• <strong>Estacionamento:</strong> Próximo</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contato;
